import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'

/**
 * Who is reading, and what pressing a mark should do.
 *
 * `EventCard` reads this rather than taking three callbacks, so a card drawn anywhere gets
 * its heart, its tick and its star without the page around it remembering to pass them.
 * That was the fault: marks were injected per call site, so the front page had them, the
 * calendar had its own inline pair that had already drifted, and the venue page and "just
 * added" had none at all — three of five places where a card appears.
 *
 * The actions live here rather than in one shared store because each page holds its own
 * payload: the front page has four windows from `/landing`, the calendar has its rows, a
 * venue page has that room's list. A heart has to write to the API *and* to whichever list
 * is on screen, and only the page knows which that is.
 *
 * No provider means no marks, which is the honest default — a card on a page that cannot
 * act on a gig should not offer to.
 */
export interface MarksActions {
  me: { isEditor: boolean } | null
  save(id: string, on: boolean): void
  going(id: string, on: boolean): void
  star(artistId: number, on: boolean): void
}

const ACTIONS = createContext<MarksActions | null>(null)

export function MarksProvider({
  value,
  children,
}: {
  value: MarksActions
  children: ReactNode
}) {
  return <ACTIONS.Provider value={value}>{children}</ACTIONS.Provider>
}

export function useMarks(): MarksActions | null {
  return useContext(ACTIONS)
}
